import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, UserCheck } from "lucide-react";

/**
 * Datos tributarios del cliente (los que piden factura, nota de crédito y guía).
 * Al escribir el RUT (o la razón social, con buscarNombre) busca en la base de clientes y autocompleta;
 * si no existe, se escriben a mano y quedan guardados al emitir el documento.
 */
const CAMPOS = [
  { k: "rut", t: "RUT", ph: "76123456-7", req: true },
  { k: "razon_social", t: "Razón social", ph: "Razón social", req: true },
  { k: "giro", t: "Giro", ph: "Giro", req: true },
  { k: "direccion", t: "Dirección", ph: "Dirección", req: true },
  { k: "comuna", t: "Comuna", ph: "Comuna" },
  { k: "ciudad", t: "Ciudad", ph: "Ciudad", req: true },
];

export const CLIENTE_VACIO = { rut: "", razon_social: "", giro: "", direccion: "", comuna: "", ciudad: "" };

/** Clientes guardados que coinciden con lo escrito en la razón social. */
function useSugerencias(texto, activo) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const q = (texto || "").trim();
    if (!activo || q.length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/customers", { params: { q, limit: 8 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [texto, activo]);
  return [items, setItems];
}

function Sugerencias({ items, prefix, onElegir }) {
  if (!items.length) return null;
  return (
    <div data-testid={`${prefix}-sugerencias`} className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
      {items.map((c) => (
        <button key={c.rut} type="button" data-testid={`${prefix}-sugerencia-${c.rut}`}
          onMouseDown={(e) => { e.preventDefault(); onElegir(c); }}
          className="w-full text-left px-3 py-2 hover:bg-sky-50 transition-colors border-b border-slate-50 last:border-0">
          <span className="block text-sm font-semibold text-slate-900 truncate">{c.razon_social}</span>
          <span className="block text-[11px] text-slate-500 font-mono-num truncate">{c.rut}{c.giro ? ` · ${c.giro}` : ""}</span>
        </button>
      ))}
    </div>
  );
}

export function ClienteTributario({ value, onChange, prefix = "cliente", className = "", inputClass = "h-10 rounded-xl", conTitulos = false, buscarNombre = false }) {
  const [buscando, setBuscando] = useState(false);
  const [encontrado, setEncontrado] = useState(false);
  const [cli, setCli] = useState(null);
  const [escribiendoNombre, setEscribiendoNombre] = useState(false);
  const [sugerencias, setSugerencias] = useSugerencias(value.razon_social, buscarNombre && escribiendoNombre && !encontrado);
  const cerrarRef = useRef(null);

  const elegir = (data) => {
    clearTimeout(cerrarRef.current);
    onChange({
      ...value, rut: data.rut, razon_social: data.razon_social || "", giro: data.giro || "",
      direccion: data.direccion || "", comuna: data.comuna || "", ciudad: data.ciudad || "",
    });
    setEncontrado(true);
    setCli(data);
    setEscribiendoNombre(false);
    setSugerencias([]);
  };

  const buscarPorRut = async () => {
    const rut = (value.rut || "").trim();
    if (rut.length < 7) return;
    setBuscando(true);
    try {
      const { data } = await api.get(`/customers/por-rut/${encodeURIComponent(rut)}`);
      elegir(data);
      toast.success(`Cliente: ${data.razon_social}`, { duration: 2000 });
    } catch (e) {
      setEncontrado(false);
      setCli(null);
      if (e?.response?.status === 404) {
        toast.info("Cliente nuevo: completa los datos y quedarán guardados", { duration: 3000 });
      }
    }
    setBuscando(false);
  };

  const esNombre = (k) => k === "razon_social" && buscarNombre;

  return (
    <div className={className} data-testid={`${prefix}-fields`}>
      {CAMPOS.map((f) => (
        <div key={f.k}>
          {conTitulos && (
            <span className="block mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {f.t}{f.req && <span className="text-rose-500"> *</span>}
            </span>
          )}
          <div className="relative">
          <Input
            data-testid={`${prefix}-${f.k.replace("_", "-")}`}
            value={value[f.k] || ""}
            onChange={(e) => {
              onChange({ ...value, [f.k]: e.target.value });
              if (f.k === "rut") setEncontrado(false);
              if (esNombre(f.k)) { setEncontrado(false); setEscribiendoNombre(true); }
            }}
            onBlur={f.k === "rut" ? buscarPorRut
              : esNombre(f.k) ? () => { cerrarRef.current = setTimeout(() => setSugerencias([]), 150); } : undefined}
            onKeyDown={f.k === "rut" ? (e) => e.key === "Enter" && buscarPorRut() : undefined}
            autoComplete={esNombre(f.k) ? "off" : undefined}
            placeholder={esNombre(f.k) ? "Escribe para buscar un cliente" : f.ph}
            className={`${inputClass} ${esNombre(f.k) ? "pr-8" : ""}`}
          />
          {f.k === "rut" && buscando && (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
          )}
          {f.k === "rut" && !buscando && encontrado && (
            <UserCheck className="w-4 h-4 text-emerald-600 absolute right-2.5 top-1/2 -translate-y-1/2" />
          )}
          {esNombre(f.k) && <Search className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />}
          {esNombre(f.k) && <Sugerencias items={sugerencias} prefix={prefix} onElegir={elegir} />}
          </div>
        </div>
      ))}
      {encontrado && cli?.sucursales?.length > 0 && (
        <div>
          {conTitulos && <span className="block mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dirección del documento</span>}
          <select data-testid={`${prefix}-sucursal`} className={`w-full border border-slate-200 bg-white px-2 text-sm ${inputClass}`}
                  onChange={(e) => {
                    const s = e.target.value === "" ? { direccion: cli.direccion, comuna: cli.comuna, ciudad: cli.ciudad } : cli.sucursales[Number(e.target.value)];
                    onChange({ ...value, direccion: s.direccion || "", comuna: s.comuna || "", ciudad: s.ciudad || "" });
                  }}>
            <option value="">Casa matriz · {cli.direccion || ""}</option>
            {cli.sucursales.map((s, i) => <option key={i} value={i}>{s.nombre} · {s.direccion}</option>)}
          </select>
        </div>)}
    </div>
  );
}

/** Devuelve el primer error de validación o null. */
export function validarCliente(c) {
  if (!(c.rut || "").trim()) return "Ingresa el RUT del cliente";
  if (!(c.razon_social || "").trim()) return "Ingresa la razón social del cliente";
  if (!(c.giro || "").trim()) return "Ingresa el giro del cliente";
  if (!(c.direccion || "").trim()) return "Ingresa la dirección del cliente";
  if (!(c.ciudad || "").trim()) return "Ingresa la ciudad del cliente";
  return null;
}
